import React, { useCallback, useEffect, useState } from 'react';
import { object, string } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { isEmpty, get } from 'lodash';
import { LazyPersonalizedSectionFetcher, Placeholder } from '@fiverr-private/personalized_content';
import { getContext } from '@fiverr-private/fiverr_context';
import { getUgcData, putUgcData } from '@fiverr-private/personalized_content/src/PersonalizedContent/externalUtils/ugcData';
import { getOriginalContent } from '../../utils/ugc/getOriginalContent';
import { getCurrentContent } from '../../utils/ugc/getCurrentContent';
import { isTranslated } from '../../utils/ugc/isTranslated';
import { buildSourceInfo } from '../../utils/ugc/buildSourceInfo';
import { updateCollections } from '../../actions/collections';
import { fetchUGC, updatePersonalizationUGC } from '../../actions/ugc';
import SafePureComponent from '../shared/SafePureComponent';
import { getConfig, SECTION_NAMES } from './constants';

import './style.scss';

const LazyPersonalizationWrapper = ({
    sectionName = SECTION_NAMES.COMPARE,
    initialPersonalizedContent,
    showTranslatedUGC,
    originalUGC,
    currentUGC,
    updateUGC,
    fetchUGC,
    ...rest
}, { general, logger }) => {
    const { gigId } = general;
    const config = getConfig(sectionName, gigId);

    const [personalizedContent, setPersonalizedContent] = useState(initialPersonalizedContent);
    const [translated, setTranslated] = useState(false);

    const enrichPersonalizationUGC = useCallback(() => {
        try {
            /*
             * return if translated is true is needed here or else the component
             * will get into an infinite loop since we're changing the state during useEffect
             * */
            if (translated) {
                return;
            }
            setTranslated(true);

            const ugcData = putUgcData(currentUGC, personalizedContent);
            setPersonalizedContent(ugcData);
        } catch (error) {
            logger.error(error, {
                description: 'failed to set ugc data from personalization content'
            });
        }
    }, [setPersonalizedContent, personalizedContent, setTranslated, translated, currentUGC, logger]);

    /*
    * Update originalUGC for Personalized content after initial Personalization fetch
    * **/
    useEffect(() => {
        const { locale } = getContext();

        if (!originalUGC && !isEmpty(personalizedContent)) {
            updateUGC(getUgcData(personalizedContent, locale), sectionName);
        }
    }, [originalUGC, personalizedContent, sectionName, updateUGC]);

    useEffect(() => {
        if (!showTranslatedUGC) {
            return;
        }

        if (!currentUGC) {
            const sourceInfo = buildSourceInfo({ componentName: `Personalization.${sectionName}`, gigId });
            fetchUGC({ sourceInfo, shouldToggle: false });
        } else {
            enrichPersonalizationUGC();
        }
    }, [showTranslatedUGC, currentUGC, enrichPersonalizationUGC, fetchUGC, sectionName, gigId]);

    const placeholder = (
        <Placeholder.GigsSectionPlaceholder className={rest.className}
            slidesToShow={config.sliderBaseNum}
            sliderBreakpoints={config.sliderBreakpoints}/>
    );

    return (
        <LazyPersonalizedSectionFetcher placeholder={placeholder}
            showTranslatedUGC={showTranslatedUGC}
            personalizedContent={personalizedContent}
            onFetchSucceeded={setPersonalizedContent}
            {...config} {...rest}/>
    );
};

LazyPersonalizationWrapper.propTypes = {
    sectionName: string
};

LazyPersonalizationWrapper.contextTypes = {
    general: object,
    logger: object
};

const mapStateToProps = ({ ugc, collections }, ownProps) => {
    const { personalization: originalUGC } = getOriginalContent({ ugc });
    const { personalization: currentUGC } = getCurrentContent({ ugc });
    const { sectionName } = ownProps;

    return {
        ...ownProps,
        originalUGC: originalUGC[sectionName],
        currentUGC: get(currentUGC, sectionName),
        showTranslatedUGC: isTranslated(ugc),
        collections
    };
};

const mapDispatchToProps = {
    updateCollections,
    updateUGC: updatePersonalizationUGC,
    fetchUGC
};

const mergeProps = ({ collections, ...restMapStateToProps }, { updateCollections, ...restMapDispatchToProps }) => ({
    ...restMapStateToProps,
    ...restMapDispatchToProps,
    collectProps: {
        collections,
        onUpdateCollections: updateCollections
    }
});

const PersonalizationWrapper = compose(
    SafePureComponent,
    connect(mapStateToProps, mapDispatchToProps, mergeProps)
)(LazyPersonalizationWrapper);

PersonalizationWrapper.SECTION_NAMES = SECTION_NAMES;

export default PersonalizationWrapper;
